/* Homepage-only components; safe to reconnect when Shopify re-renders a section. */
(() => {
  if (!window.customElements) return;
  if (!customElements.get('mr-home-hero')) {
    class MRHomeHero extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._abort = new AbortController();
        this._slides = Array.from(this.querySelectorAll('[data-mrh-slide]'));
        this._media = Array.from(this.querySelectorAll('[data-mrh-media]'));
        this._selectors = Array.from(this.querySelectorAll('[data-mrh-select]'));
        this._play = this.querySelector('[data-mrh-play]');
        this._count = this.querySelector('[data-mrh-current]');
        this._announcement = this.querySelector('[data-mrh-announcement]');
        this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this._index = 0;
        this._elapsed = 0;
        this._frame = null;
        this._inView = true;
        this._hovered = false;
        this._focused = false;
        this._menuOpen = false;
        this._interval = Math.max(5000, Number(this.dataset.interval) || 9000);
        this._playing = this._slides.length > 1 && this.dataset.autoplay === 'true' && !this._motion.matches && !window.Shopify?.designMode;
        if (!this._slides.length) return;
        const listen = (node, type, fn, options = {}) => node?.addEventListener(type, fn, {...options, signal:this._abort.signal});
        listen(this, 'click', event => {
          const selector = event.target.closest('[data-mrh-select]');
          if (selector) this.select(Number(selector.dataset.mrhSelect), true);
          else if (event.target.closest('[data-mrh-previous]')) this.select(this._index - 1, true);
          else if (event.target.closest('[data-mrh-next]')) this.select(this._index + 1, true);
          else if (event.target.closest('[data-mrh-play]')) {
            this._playing = !this._playing;
            this._hovered = false;
            this._focused = false;
            this.syncPlayback();
          }
        });
        listen(this, 'keydown', event => {
          if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.closest('input,textarea,select')) return;
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            this.select(this._index + (event.key === 'ArrowRight' ? 1 : -1), true);
          }
        });
        listen(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this._hovered = true; this.syncPlayback(); } });
        listen(this, 'pointerleave', event => { if (event.pointerType === 'mouse') { this._hovered = false; this.syncPlayback(); } });
        listen(this, 'focusin', () => { this._focused = true; this.syncPlayback(); });
        listen(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this._focused = false; this.syncPlayback(); } });
        listen(this, 'touchstart', event => { this._touch = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null; }, {passive:true});
        listen(this, 'touchcancel', () => { this._touch = null; }, {passive:true});
        listen(this, 'touchend', event => {
          if (!this._touch || !event.changedTouches.length) return;
          const dx = event.changedTouches[0].clientX - this._touch.x;
          const dy = event.changedTouches[0].clientY - this._touch.y;
          this._touch = null;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) this.select(this._index + (dx < 0 ? 1 : -1), true);
        }, {passive:true});
        listen(document, 'visibilitychange', () => this.syncPlayback());
        listen(document, 'mrh:menu-toggle', event => { this._menuOpen = event.detail.open; this.syncPlayback(); });
        listen(this._motion, 'change', () => { if (this._motion.matches) this._playing = false; this.syncPlayback(); });
        listen(document, 'shopify:block:select', event => {
          if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
          const index = this._slides.findIndex(slide => slide.dataset.blockId === String(event.detail.blockId));
          if (index >= 0) this.select(index, true);
        });
        listen(document, 'shopify:section:select', event => {
          if (String(event.detail?.sectionId) === this.dataset.sectionId) { this._playing = false; this.syncPlayback(); }
        });
        if ('IntersectionObserver' in window) {
          this._observer = new IntersectionObserver(entries => { this._inView = entries[0].isIntersecting; this.syncPlayback(); }, {threshold:0.05});
          this._observer.observe(this);
        }
        this.select(0, false);
        this.classList.add('is-ready');
      }
      select(index, manual) {
        if (!this._slides?.length) return;
        this._index = (index + this._slides.length) % this._slides.length;
        this._elapsed = 0;
        if (manual) this._playing = false;
        this._slides.forEach((slide, i) => { slide.hidden = i !== this._index; slide.classList.toggle('is-active', i === this._index); });
        this._media.forEach((media, i) => media.classList.toggle('is-active', i === this._index));
        this._selectors.forEach((button, i) => { button.setAttribute('aria-pressed', String(i === this._index)); button.classList.toggle('is-selected', i === this._index); });
        if (this._count) this._count.textContent = String(this._index + 1).padStart(2,'0');
        if (manual && this._announcement) this._announcement.textContent = `${this._slides[this._index].dataset.title}, slide ${this._index + 1} of ${this._slides.length}`;
        this.syncPlayback();
      }
      syncPlayback() {
        if (this._frame !== null) cancelAnimationFrame(this._frame);
        this._frame = null;
        if (!this._connected || !this._slides?.length) return;
        if (this._play) {
          this._play.setAttribute('aria-label', this._playing ? 'Pause slideshow' : 'Play slideshow');
          this._play.querySelector('[data-mrh-play-icon]').hidden = this._playing;
          this._play.querySelector('[data-mrh-pause-icon]').hidden = !this._playing;
        }
        this.paintProgress();
        if (!this._playing || this._hovered || this._focused || this._menuOpen || !this._inView || document.hidden) return;
        let previous = performance.now();
        const tick = now => {
          this._elapsed += now - previous;
          previous = now;
          if (this._elapsed >= this._interval) { this.select(this._index + 1, false); return; }
          this.paintProgress();
          this._frame = requestAnimationFrame(tick);
        };
        this._frame = requestAnimationFrame(tick);
      }
      paintProgress() {
        this._selectors?.forEach((button, i) => {
          const fill = button.querySelector('.mrh-progress');
          fill.style.width = i === this._index ? `${this._playing ? Math.min(100, this._elapsed / this._interval * 100) : 100}%` : '0%';
        });
      }
      disconnectedCallback() {
        this._connected = false;
        this._abort?.abort();
        this._observer?.disconnect();
        if (this._frame !== null) cancelAnimationFrame(this._frame);
      }
    }
    customElements.define('mr-home-hero', MRHomeHero);
  }
  if (!customElements.get('mr-home-header')) {
    class MRHomeHeader extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._abort = new AbortController();
        this._dialog = this.querySelector('dialog');
        this._open = this.querySelector('[data-mrh-open-menu]');
        const listen = (node, type, fn) => node?.addEventListener(type, fn, {signal:this._abort.signal});
        listen(this._open, 'click', () => {
          if (this._dialog.open) return;
          this._dialog.showModal();
          this._open.setAttribute('aria-expanded','true');
          this._priorOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          this._locked = true;
          document.dispatchEvent(new CustomEvent('mrh:menu-toggle',{detail:{open:true}}));
        });
        listen(this.querySelector('[data-mrh-close-menu]'), 'click', () => this._dialog.close());
        listen(this._dialog, 'click', event => {
          if (event.target !== this._dialog) return;
          const bounds = this._dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this._dialog.close();
        });
        listen(this._dialog, 'close', () => {
          this.releaseScroll();
          this._open.setAttribute('aria-expanded','false');
          document.dispatchEvent(new CustomEvent('mrh:menu-toggle',{detail:{open:false}}));
          this._open.focus({preventScroll:true});
        });
      }
      releaseScroll() {
        if (this._locked) document.documentElement.style.overflow = this._priorOverflow;
        this._locked = false;
      }
      disconnectedCallback() {
        this._connected = false;
        this._abort?.abort();
        if (this._dialog?.open) this._dialog.close();
        this.releaseScroll();
        document.dispatchEvent(new CustomEvent('mrh:menu-toggle',{detail:{open:false}}));
      }
    }
    customElements.define('mr-home-header', MRHomeHeader);
  }
})();
